/**
 * Cookie Consent Utility
 * Manages cookie preferences and location services
 */

export type CookiePreference = 'all' | 'necessary' | null;

export interface CookiePreferences {
  preference: CookiePreference;
  locationEnabled: boolean;
  timestamp: number;
}

const COOKIE_CONSENT_KEY = 'cookie-consent-preference';
const COOKIE_EXPIRY_HOURS = 1; // 1 hour
const COOKIE_EXPIRY_MS = COOKIE_EXPIRY_HOURS * 60 * 60 * 1000; // Convert to milliseconds

/**
 * Refresh cookie timestamp to extend expiry
 */
function refreshCookieTimestamp(preferences: CookiePreferences): void {
  if (typeof window === 'undefined') return;
  
  try {
    const refreshed: CookiePreferences = {
      ...preferences,
      timestamp: Date.now(), // Update timestamp to current time
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(refreshed));
  } catch (error) {
    console.error('Error refreshing cookie timestamp:', error);
  }
}

/**
 * Get cookie consent preference from localStorage
 * Automatically refreshes the cookie if it's still valid
 */
export function getCookiePreference(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as CookiePreferences;
    
    // Check if preference is still valid (not expired)
    const now = Date.now();
    const expiryTime = parsed.timestamp + COOKIE_EXPIRY_MS;
    
    if (now > expiryTime) {
      // Preference expired, remove it
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      return null;
    }

    // Auto-refresh: Update timestamp to extend expiry by another hour
    // This ensures cookies refresh automatically every time they're accessed
    refreshCookieTimestamp(parsed);
    
    // Return refreshed preferences
    return {
      ...parsed,
      timestamp: Date.now(),
    };
  } catch (error) {
    console.error('Error reading cookie preference:', error);
    return null;
  }
}

/**
 * Save cookie consent preference
 */
export function saveCookiePreference(preference: CookiePreference): void {
  if (typeof window === 'undefined') return;

  try {
    const preferences: CookiePreferences = {
      preference,
      locationEnabled: true, // Location is always enabled in both modes
      timestamp: Date.now(),
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error('Error saving cookie preference:', error);
  }
}

/**
 * Check if user has given consent
 */
export function hasCookieConsent(): boolean {
  const prefs = getCookiePreference();
  return prefs !== null;
}

/**
 * Check if location services are enabled
 * Location is enabled in both "all" and "necessary" modes
 */
export function isLocationEnabled(): boolean {
  const prefs = getCookiePreference();
  if (!prefs) return false;
  
  // Location is always enabled when consent is given (both modes)
  return prefs.locationEnabled;
}

/**
 * Check if analytics cookies are allowed
 * Only allowed when preference is "all"
 */
export function areAnalyticsAllowed(): boolean {
  const prefs = getCookiePreference();
  return prefs?.preference === 'all';
}

/**
 * Request location permission
 */
export function requestLocationPermission(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      resolve,
      reject,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes cache
      }
    );
  });
}

/**
 * Set up automatic cookie refresh
 * Refreshes cookies every hour to keep them active
 * Returns cleanup function to stop the interval
 */
export function setupAutoCookieRefresh(): () => void {
  if (typeof window === 'undefined') {
    return () => {}; // Return no-op cleanup function
  }

  // Refresh interval: every 50 minutes (slightly less than 1 hour to ensure cookies never expire)
  const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes

  const refreshCookies = () => {
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!stored) return;

      const parsed = JSON.parse(stored) as CookiePreferences;
      const now = Date.now();
      const expiryTime = parsed.timestamp + COOKIE_EXPIRY_MS;

      // Only refresh if cookie is still valid
      if (now < expiryTime) {
        refreshCookieTimestamp(parsed);
      }
    } catch (error) {
      console.error('Error in auto-refresh:', error);
    }
  };

  // Set up interval to refresh cookies automatically
  const intervalId = setInterval(refreshCookies, REFRESH_INTERVAL_MS);

  // Return cleanup function
  return () => {
    clearInterval(intervalId);
  };
}

